import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, Calendar, Clock, Check, X, ChevronLeft, ChevronRight, Phone } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_TIMES = ["09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const COURSE_NAMES = {
  technical_analysis: { EN: "Technical Analysis", RU: "Технический Анализ", ES: "Análisis Técnico" },
  investment_base: { EN: "Investment Base", RU: "Инвестиционная База", ES: "Base de Inversión" },
  full_mentor: { EN: "Full Mentor", RU: "Полный Менторинг", ES: "Mentor Completo" },
};

const LABELS = {
  EN: {
    back: "Back to Dashboard",
    lessonsLeft: "lessons remaining",
    schedule: "Schedule a Lesson",
    selectDate: "Select a date",
    selectTime: "Select a time slot",
    book: "Book Lesson",
    booked: "Your Bookings",
    noBookings: "No bookings yet. Schedule your first lesson!",
    cancel: "Cancel",
    cancelled: "Cancelled",
    confirmed: "Confirmed",
    booking: "Booking...",
    noLessons: "No lessons remaining",
    today: "Today",
    months: ["January","February","March","April","May","June","July","August","September","October","November","December"],
    days: ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"],
    contactTitle: "One more step",
    contactSubtitle: "Enter your preferred contact number so we can coordinate your lesson.",
    contactPlaceholder: "+1 234 567 8900 (WhatsApp / Telegram)",
    contactConfirm: "Confirm & Book",
    contactGdpr: "By submitting, you agree to share your contact info for lesson coordination.",
    contactSaving: "Saving...",
    timezone: "All available slots are displayed in Lisbon time (WET / GMT+1)",
    cancelConfirm: "Cancel this lesson?",
    cancelNoRefund: "This lesson is less than 12 hours away. Cancelling will NOT refund your lesson credit. Proceed?",
  },
  RU: {
    back: "Назад",
    lessonsLeft: "уроков осталось",
    schedule: "Запланировать урок",
    selectDate: "Выберите дату",
    selectTime: "Выберите время",
    book: "Забронировать",
    booked: "Ваши бронирования",
    noBookings: "Нет бронирований. Запланируйте свой первый урок!",
    cancel: "Отменить",
    cancelled: "Отменён",
    confirmed: "Подтверждён",
    booking: "Бронирование...",
    noLessons: "Уроков не осталось",
    today: "Сегодня",
    months: ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"],
    days: ["Вс","Пн","Вт","Ср","Чт","Пт","Сб"],
    contactTitle: "Ещё один шаг",
    contactSubtitle: "Укажите номер для связи, чтобы мы могли координировать урок.",
    contactPlaceholder: "+7 999 123 4567 (WhatsApp / Telegram)",
    contactConfirm: "Подтвердить и забронировать",
    contactGdpr: "Отправляя данные, вы соглашаетесь поделиться контактной информацией для координации уроков.",
    contactSaving: "Сохранение...",
    timezone: "Все слоты отображаются по времени Лиссабона (WET / GMT+1)",
    cancelConfirm: "Отменить этот урок?",
    cancelNoRefund: "До урока менее 12 часов. Отмена НЕ вернёт кредит за урок. Продолжить?",
  },
  ES: {
    back: "Volver",
    lessonsLeft: "lecciones restantes",
    schedule: "Programar una lección",
    selectDate: "Selecciona una fecha",
    selectTime: "Selecciona un horario",
    book: "Reservar",
    booked: "Tus reservas",
    noBookings: "Sin reservas. ¡Programa tu primera lección!",
    cancel: "Cancelar",
    cancelled: "Cancelado",
    confirmed: "Confirmado",
    booking: "Reservando...",
    noLessons: "No quedan lecciones",
    today: "Hoy",
    months: ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"],
    days: ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"],
    contactTitle: "Un paso más",
    contactSubtitle: "Ingresa tu número de contacto preferido para coordinar tu lección.",
    contactPlaceholder: "+34 612 345 678 (WhatsApp / Telegram)",
    contactConfirm: "Confirmar y reservar",
    contactGdpr: "Al enviar, aceptas compartir tu información de contacto para la coordinación de lecciones.",
    contactSaving: "Guardando...",
    timezone: "Todos los horarios se muestran en hora de Lisboa (WET / GMT+1)",
    cancelConfirm: "¿Cancelar esta lección?",
    cancelNoRefund: "Faltan menos de 12 horas para la lección. Cancelar NO reembolsará tu crédito. ¿Continuar?",
  },
};

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export default function CoursePage({ session, courseId, onBack, activeLang }) {
  const l = LABELS[activeLang] || LABELS.EN;
  const courseName = COURSE_NAMES[courseId]?.[activeLang] || courseId;

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [lessonsRemaining, setLessonsRemaining] = useState(10);
  const [loading, setLoading] = useState(false);
  const [globalBookedSlots, setGlobalBookedSlots] = useState([]);
  const [userPhone, setUserPhone] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  const headers = { Authorization: `Bearer ${session.access_token}` };

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, bookingsRes, slotsRes] = await Promise.all([
        fetch(`${API_URL}/api/auth/me`, { headers }),
        fetch(`${API_URL}/api/bookings`, { headers }),
        fetch(`${API_URL}/api/bookings/slots`, { headers }),
      ]);
      if (profileRes.ok) {
        const p = await profileRes.json();
        setLessonsRemaining(p.lessons_remaining);
        setUserPhone(p.phone || null);
      }
      if (bookingsRes.ok) {
        const b = await bookingsRes.json();
        setBookings(b);
      }
      if (slotsRes.ok) {
        const s = await slotsRes.json();
        setGlobalBookedSlots(s.map(slot => `${slot.date}_${slot.time}`));
      }
    } catch (e) { /* silent */ }
  }, [session.access_token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const doBook = async () => {
    if (!selectedDate || !selectedTime || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bookings`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ course: courseId, date: selectedDate, time: selectedTime, ref_code: localStorage.getItem("alvo13_ref") || null }),
      });
      if (res.ok) {
        setSelectedDate(null);
        setSelectedTime(null);
        await fetchData();
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  const handleBook = () => {
    if (!selectedDate || !selectedTime || loading) return;
    if (userPhone) {
      doBook();
    } else {
      setShowContactModal(true);
    }
  };

  const handleContactSubmit = async () => {
    if (!phoneInput.trim() || savingPhone) return;
    setSavingPhone(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phoneInput.trim() }),
      });
      if (res.ok) {
        setUserPhone(phoneInput.trim());
        setShowContactModal(false);
        setPhoneInput("");
        await doBook();
      }
    } catch (e) { /* silent */ }
    setSavingPhone(false);
  };

  const handleCancel = async (bookingId, lessonDate, lessonTime) => {
    const lessonDt = new Date(`${lessonDate}T${lessonTime}:00Z`);
    const hoursUntil = (lessonDt - new Date()) / (1000 * 60 * 60);
    const msg = hoursUntil < 12 ? l.cancelNoRefund : l.cancelConfirm;
    if (!window.confirm(msg)) return;
    try {
      const res = await fetch(`${API_URL}/api/bookings/${bookingId}`, { method: "DELETE", headers });
      if (res.ok) await fetchData();
    } catch (e) { /* silent */ }
  };

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
    else setCurrentMonth(currentMonth - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
    else setCurrentMonth(currentMonth + 1);
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const isDateBookable = (day) => {
    const d = new Date(currentYear, currentMonth, day);
    const dayOfWeek = d.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) return false;
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return dateStr >= todayStr;
  };

  const bookedSlots = globalBookedSlots;
  const confirmedBookings = bookings.filter(b => b.status === "confirmed" && b.lesson_date >= todayStr);
  const pastBookings = bookings.filter(b => b.status !== "confirmed" || b.lesson_date < todayStr);

  return (
    <div className="course-page" data-testid="course-page">
      <button className="course-back-btn" onClick={onBack} data-testid="course-back-btn">
        <ArrowLeft className="w-4 h-4" /> {l.back}
      </button>

      <div className="course-header" data-testid="course-header">
        <h1 className="course-title">{courseName}</h1>
        <div className="course-lessons-badge" data-testid="lessons-remaining">
          <Calendar className="w-4 h-4" />
          <span className="course-lessons-count">{lessonsRemaining}</span>
          <span>{l.lessonsLeft}</span>
        </div>
      </div>

      <div className="course-layout">
        {/* Calendar */}
        <div className="course-calendar-section" data-testid="calendar-section">
          <h2 className="course-section-title">{l.schedule}</h2>

          <div className="cal-container">
            <div className="cal-nav">
              <button onClick={prevMonth} className="cal-nav-btn" data-testid="cal-prev"><ChevronLeft className="w-5 h-5" /></button>
              <span className="cal-month">{l.months[currentMonth]} {currentYear}</span>
              <button onClick={nextMonth} className="cal-nav-btn" data-testid="cal-next"><ChevronRight className="w-5 h-5" /></button>
            </div>
            <div className="cal-grid" data-testid="cal-grid">
              {l.days.map(d => <div key={d} className="cal-day-label">{d}</div>)}
              {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} className="cal-cell empty" />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const bookable = isDateBookable(day);
                const isSelected = selectedDate === dateStr;
                const isToday = dateStr === todayStr;
                const hasBooking = confirmedBookings.some(b => b.lesson_date === dateStr);
                return (
                  <button
                    key={day}
                    className={`cal-cell ${bookable ? "bookable" : "disabled"} ${isSelected ? "selected" : ""} ${isToday ? "today" : ""} ${hasBooking ? "has-booking" : ""}`}
                    onClick={() => bookable && setSelectedDate(dateStr)}
                    disabled={!bookable}
                    data-testid={`cal-day-${day}`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Slots */}
          {selectedDate && (
            <div className="time-slots" data-testid="time-slots">
              <p className="time-slots-label">{l.selectTime}</p>
              <p className="time-slots-timezone" data-testid="timezone-label">{l.timezone}</p>
              <div className="time-grid">
                {AVAILABLE_TIMES.map(time => {
                  const slotKey = `${selectedDate}_${time}`;
                  const isBooked = bookedSlots.includes(slotKey);
                  return (
                    <button
                      key={time}
                      className={`time-slot ${selectedTime === time ? "selected" : ""} ${isBooked ? "booked" : ""}`}
                      onClick={() => !isBooked && setSelectedTime(time)}
                      disabled={isBooked}
                      data-testid={`time-slot-${time.replace(":", "")}`}
                    >
                      <Clock className="w-3.5 h-3.5" /> {time}
                    </button>
                  );
                })}
              </div>
              {selectedTime && (
                <button
                  className="book-btn"
                  onClick={handleBook}
                  disabled={loading || lessonsRemaining <= 0}
                  data-testid="book-lesson-btn"
                >
                  {loading ? l.booking : lessonsRemaining <= 0 ? l.noLessons : l.book}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Bookings List */}
        <div className="course-bookings-section" data-testid="bookings-section">
          <h2 className="course-section-title">{l.booked}</h2>
          {confirmedBookings.length === 0 && pastBookings.length === 0 ? (
            <p className="no-bookings">{l.noBookings}</p>
          ) : (
            <div className="bookings-list">
              {confirmedBookings.map(b => (
                <div key={b.id} className="booking-card confirmed" data-testid={`booking-${b.id}`}>
                  <div className="booking-info">
                    <div className="booking-date"><Calendar className="w-4 h-4" /> {b.lesson_date}</div>
                    <div className="booking-time"><Clock className="w-4 h-4" /> {b.lesson_time}</div>
                  </div>
                  <div className="booking-actions">
                    <span className="booking-status confirmed"><Check className="w-3 h-3" /> {l.confirmed}</span>
                    <button className="booking-cancel" onClick={() => handleCancel(b.id, b.lesson_date, b.lesson_time)} data-testid={`cancel-${b.id}`}>
                      <X className="w-3.5 h-3.5" /> {l.cancel}
                    </button>
                  </div>
                </div>
              ))}
              {pastBookings.map(b => (
                <div key={b.id} className="booking-card past">
                  <div className="booking-info">
                    <div className="booking-date"><Calendar className="w-4 h-4" /> {b.lesson_date}</div>
                    <div className="booking-time"><Clock className="w-4 h-4" /> {b.lesson_time}</div>
                  </div>
                  <span className="booking-status cancelled">{b.status === "cancelled" ? l.cancelled : l.confirmed}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="contact-modal-overlay" data-testid="contact-modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="contact-modal" data-testid="contact-modal" onClick={e => e.stopPropagation()}>
            <button className="contact-modal-close" onClick={() => setShowContactModal(false)} data-testid="contact-modal-close">
              <X className="w-4 h-4" />
            </button>
            <div className="contact-modal-icon">
              <Phone className="w-6 h-6" />
            </div>
            <h3 className="contact-modal-title">{l.contactTitle}</h3>
            <p className="contact-modal-subtitle">{l.contactSubtitle}</p>
            <input
              type="tel"
              className="contact-modal-input"
              placeholder={l.contactPlaceholder}
              value={phoneInput}
              onChange={e => setPhoneInput(e.target.value)}
              autoFocus
              data-testid="contact-phone-input"
            />
            <p className="contact-modal-gdpr">{l.contactGdpr}</p>
            <button
              className="contact-modal-submit"
              onClick={handleContactSubmit}
              disabled={!phoneInput.trim() || savingPhone}
              data-testid="contact-submit-btn"
            >
              {savingPhone ? l.contactSaving : l.contactConfirm}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
